import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-14139" data-mp="1rnfggsrutct">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-14140" data-mp="6nub9grgeoh1">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-14141" data-mp="fpv5e4re4ueh">
              <span className="text-xs font-bold text-indigo-800" data-id="element-14142" data-mp="fp28dvi8hj9j">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-14143" data-mp="pfn3va2m27o8">
            <div className="text-center" data-id="element-14144" data-mp="phhpkqlpi3td">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-14145" data-mp="uezso0d1lotg">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-14146" data-mp="yhe6u75ro0ee">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-14147" data-mp="13fxwoinhdwc1">→</div>
            <div className="text-center" data-id="element-14148" data-mp="2zi1b3oa0lg1p">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-14149" data-mp="2zj42dxb91ki5">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-14150" data-mp="2vik3ao7486td">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-14151" data-mp="34h5h661n6xnt">
            <table className="w-full text-sm" data-id="element-14152" data-mp="3j5s5uyd5t1rp">
              <thead data-id="element-14153" data-mp="3j4gvugt4exs5">
                <tr className="bg-indigo-100/70" data-id="element-14154" data-mp="3e777z5ldqu6f">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-14155" data-mp="3e6f9nn0eeicg">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-14156" data-mp="3s4lzqgz3ve1k">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-14157" data-mp="1xt7bk4lqtmnx">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-14158" data-mp="dbfwopwz6mhs" data-mpi={index}>
                      <td className="px-3 py-1.5" data-id="element-14159" data-mp="3cs4kif2feyul">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-14160" data-mp="3gsoe58nsjkbz">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-14161" data-mp="q9m2mtfynv74">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14162" data-mp="q9405f2pgkqr">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-14163" data-mp="2j9qt7hlheb9">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-14164" data-mp="3tqcgtvlp5ejw">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-14165" data-mp="3ost2kvp011ry">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-14166" data-mp="3js5bka0rskxq">
            <span className="text-2xl" data-id="element-14167" data-mp="3fqbgqid9ab5h">📝</span>
            <div className="text-left" data-id="element-14168" data-mp="3fso6hhp99pt3">
              <div className="text-sm font-bold text-indigo-900" data-id="element-14169" data-mp="1v92akzsc7ho5">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-14170" data-mp="1z8jevpw128rq">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-14171" data-mp="297s5c8rop59c">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-14172" data-mp="1bll14zzalexi">
            <div className="text-center" data-id="element-14173" data-mp="1gj3er3o98ibu">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-14174" data-mp="1gk661cphomsa">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-14175" data-mp="1q963uss99c4w">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-14176" data-mp="su1zoxkx28xl">→</div>
            <div className="text-center" data-id="element-14177" data-mp="stiqc59c0sv6">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-14178" data-mp="xsvectbxw5zc">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-14179" data-mp="pre2vl5242iz">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-14180" data-mp="zi8w59gq02dx">
          <table className="w-full" data-id="element-14181" data-mp="ujfldostiphg">
            <thead data-id="element-14182" data-mp="ukguhnq9x1wj">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-14183" data-mp="ujxn30y91vwr">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-14184" data-mp="6ucstlwnbc86">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-14185" data-mp="2u2pyi45h1xj">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-14186" data-mp="brwjxmefzjhj">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-14187" data-mp="1n7l82h3fmojx" data-mpi={index}>
                    <td className="px-4 py-3" data-id="element-14188" data-mp="3svk4rki64n3k">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-14189" data-mp="3su91rmwjrjdi">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-14190" data-mp="bn6y6tlm2fda">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14191" data-mp="blcf7ktwgxbu">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-14192" data-mp="22ri7u4meiihc">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-14193" data-mp="1xsx7m575fysw">
            <div className="flex items-start gap-3" data-id="element-14194" data-mp="2vfwbazw9e70a">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-14195" data-mp="2vdjr0g313kk2">
                <span className="text-blue-600 text-lg" data-id="element-14196" data-mp="2lhghuq9relnm">🔍</span>
              </div>
              <div data-id="element-14197" data-mp="2ljjeht5f3d1b">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-14198" data-mp="2hg50t94yb8r1">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14199" data-mp="1a3conmazx1jm">
                  De <strong data-id="element-14200" data-mp="160y50zzi5xra">stam</strong> krijg je door <strong data-id="element-14201" data-mp="15zwuqxqb9fam">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-14202" data-mp="3hpcpi61unq7k">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-14203" data-mp="3qnos76fzqegd">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14204" data-mp="976yfz43b6nh">
              <div className="flex items-start gap-3" data-id="element-14205" data-mp="dawj4dhn9438">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14206" data-mp="i8xzgqnoqpb0">
                  <span className="text-emerald-600 text-lg" data-id="element-14207" data-mp="2f1q1t9mxi1fg">📋</span>
                </div>
                <div data-id="element-14208" data-mp="2k01avi5eg7wm">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14209" data-mp="2jzhwgbeyk1nc">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14210" data-mp="7rfusuy4jwjo">
                    <p data-id="element-14211" data-mp="coodqxr3k4lz">
                      <strong data-id="element-14212" data-mp="cq96chtupdh7">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14213" data-mp="hlxzo3fa1y52">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-14214" data-mp="qoh3apqw0esd">
                      <strong data-id="element-14215" data-mp="qn5ycmlph0zo">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14216" data-mp="vn0nolpu1iim">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14217" data-mp="10eion9ruvj3l">
                      <strong data-id="element-14218" data-mp="10gczfv9exuty">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14219" data-mp="15ck6xshgjee0">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14220" data-mp="1ediak9nzhjw2">
                      <strong data-id="element-14221" data-mp="1eetfict60xor">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14222" data-mp="2ywc2asd8olz4">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14223" data-mp="2kw1c7rjr7bf0">
              <div className="flex items-start gap-3" data-id="element-14224" data-mp="3ijtkbgkcnx1d">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14225" data-mp="3ija2dzup98nc">
                  <span className="text-emerald-600 text-lg" data-id="element-14226" data-mp="3dmtff8z8l0bu">📋</span>
                </div>
                <div data-id="element-14227" data-mp="38s5oz4ko9vum">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14228" data-mp="38rnjrig187ak">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14229" data-mp="55os0lg5y0zg">
                    <p data-id="element-14230" data-mp="d5grv4y7hxt">
                      <strong data-id="element-14231" data-mp="cvq05c2x6r2">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-14232" data-mp="1unc1x9vnhi90">
                      <strong data-id="element-14233" data-mp="1uo58oaum07r5">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14234" data-mp="1ymui5scj155i">
                      <em data-id="element-14235" data-mp="1kvz7qbmdqfzt">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14236" data-mp="1fv1t290kw432">
              <div className="flex items-start gap-3" data-id="element-14237" data-mp="11zg2hxep5c8j">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14238" data-mp="16xhmqlnuzhll">
                  <span className="text-emerald-600 text-lg" data-id="element-14239" data-mp="2wbxkoqkohm6b">📋</span>
                </div>
                <div data-id="element-14240" data-mp="31968p7sickfh">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14241" data-mp="318elak92dt0a">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14242" data-mp="36furytuedr8r">
                    <p data-id="element-14243" data-mp="3k6hi3xm3e8t8">
                      <strong data-id="element-14244" data-mp="3k7riitgf3t31">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-14245" data-mp="3feyds5ky8kqy">
                      <strong data-id="element-14246" data-mp="3fegawq3s78a2">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14247" data-mp="3u3cd46j5pr2r">
                      <em data-id="element-14248" data-mp="3p5avlq1dpzte">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-14249" data-mp="28tdvfqthnho2">
            <div className="flex items-start gap-3" data-id="element-14250" data-mp="24sbw79zkn5m3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-14251" data-mp="26639niyhkwyd">
                <span className="text-amber-600 text-lg" data-id="element-14252" data-mp="2b4o7jbq9n4jn">💡</span>
              </div>
              <div data-id="element-14253" data-mp="2f8vwna0mymie">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-14254" data-mp="2f8m87qv8onet">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14255" data-mp="2k4jq41m5i1tp">
                  Bij <strong data-id="element-14256" data-mp="1nihrq3alokiv">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-14257" data-mp="1rirz9ke1jizu">jij loopt</em> wordt{' '}
                  <em data-id="element-14258" data-mp="1whlaf65uuxwu">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}